import { useEffect, useCallback } from 'react';
import { useApp } from '@/contexts/AppContext';
import { toast } from 'sonner';

/**
 * Hook to handle global keyboard shortcuts for note actions
 */
export function useNoteShortcuts() {
  const {
    selectedNoteId,
    selectedNote,
    togglePin,
    toggleFavorite,
    archiveNote,
    deleteNote,
    activeView,
  } = useApp();

  const handleKeyDown = useCallback((e) => {
    // Skip if in input/textarea/contenteditable
    const isEditing = 
      e.target.tagName === 'INPUT' || 
      e.target.tagName === 'TEXTAREA' || 
      e.target.isContentEditable;

    // Only handle shortcuts with Ctrl/Cmd + Shift
    if (!e.ctrlKey && !e.metaKey) return;
    if (!e.shiftKey) return;

    // Need a selected note for most actions
    if (!selectedNoteId) return;

    const key = e.key.toLowerCase();

    switch (key) {
      case 'p': // Toggle pin
        e.preventDefault();
        togglePin(selectedNoteId);
        toast.success(selectedNote?.is_pinned ? 'Note unpinned' : 'Note pinned');
        break;

      case 'f': // Toggle favorite
        e.preventDefault();
        toggleFavorite(selectedNoteId);
        toast.success(selectedNote?.is_favorited ? 'Removed from favorites' : 'Added to favorites');
        break;

      case 'a': // Archive note
        e.preventDefault();
        if (activeView !== 'trash' && activeView !== 'archived') {
          archiveNote(selectedNoteId);
          toast.success('Note archived');
        }
        break;

      case 'd': // Delete note (move to trash)
        e.preventDefault();
        if (activeView !== 'trash') {
          deleteNote(selectedNoteId);
          toast.success('Note moved to trash');
        }
        break;

      default:
        break;
    }
  }, [selectedNoteId, selectedNote, togglePin, toggleFavorite, archiveNote, deleteNote, activeView]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}

/**
 * Hook to handle editor-specific keyboard shortcuts
 */
export function useEditorShortcuts(editor, { onSave, onShare, onExportPDF }) {
  const handleKeyDown = useCallback((e) => {
    if (!editor) return;

    // Ctrl/Cmd + S = Force save
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's' && !e.shiftKey) {
      e.preventDefault();
      onSave?.();
      toast.success('Note saved');
      return;
    }

    // Ctrl/Cmd + Shift + L = Share
    if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'l') {
      e.preventDefault();
      onShare?.();
      return;
    }

    // Ctrl/Cmd + Shift + E = Export as PDF
    if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'e') {
      e.preventDefault();
      onExportPDF?.();
      return;
    }
  }, [editor, onSave, onShare, onExportPDF]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}
